import React, { useState } from "react";
import styled from "styled-components";
import { Theme } from "../../../common/Theme";
import CustomInput from "../../../common/components/input/CustomInput";
import CustomSegmentedControl from "../../../common/components/button/CustomSegmentedControl";
import CustomRadios from "../../../common/components/radio/CustomRadios";
import Translate from "../../../common/translate/Translate";

// 전체 레이아웃을 감싸는 컨테이너
const Container = styled.div`
    width: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    border-radius: 12px;
    box-sizing: border-box;
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: ${({ length }) => `repeat(${length}, 1fr)`};
    align-items: center;
    min-height: 35px;
    background-color: #f6f6f6;
    border-radius: 8px;
`;

// 각 행(Row)의 기본 스타일
const Row = styled.div`
    display: grid;
    grid-template-columns: ${({ length }) => `repeat(${length}, 1fr)`};
    align-items: center;
    min-height: 40px;
    padding: 8px 0;
`;

// 테이블 헤더
const TableHeader = styled.div`
    text-align: center;
    font-weight: 500;
    color: #4a4a4a;
    font-size: ${(props) => props.fontSize || Theme.text.c3.fontSize};
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    letter-spacing: ${(props) => props.letterSpacing || Theme.text.default.letterSpacing};
    line-height: ${(props) => props.lineHeight || Theme.text.default.lineHeight};
`;

// 행 헤더
const RowHeader = styled.div`
    padding-left: 8px;
    color: #33373c;
    font-family: ${(props) => props.fontFamily || Theme.text.default.fontFamily};
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
`;

// 실제 컴포넌트가 들어갈 셀
const Cell = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 8px;
    min-width: 126px;
`;

// 행과 행을 구분하는 라인
const Divider = styled.hr`
    border: 0;
    border-top: 1px solid #e0e0e0;
    margin: 4px 0;
`;

// 컬럼 헤더 데이터
const defaultColumnHeaders = [
    { label: "Hot", value: "hot" },
    { label: "Warm", value: "warm" },
    { label: "Cold", value: "cold" },
    { label: "Delicates", value: "delicates" },
];

// 행 데이터
const defaultRowData = [
    { label: "Washing Time", value: "washingTime" },
    { label: "Water Level", value: "waterLevel" },
    { label: "Rinsing Time", value: "rinsingTime" },
    { label: "Rinsing Count", value: "rinsingCount" },
    { label: "Top Spin Speed", value: "topSpinSpeed" },
    { label: "Twin Spray", value: "twinSpray" },
];

const mockData = [
    { washingTime: 8, waterLevel: "low", rinsingTime: 8, rinsingCount: 1, topSpinSpeed: "Max" },
    { washingTime: 9, waterLevel: "low", rinsingTime: 9, rinsingCount: 2, topSpinSpeed: "1000" },
    { washingTime: 9, waterLevel: "low", rinsingTime: 9, rinsingCount: 3, topSpinSpeed: "900" },
    { washingTime: 9, waterLevel: "low", rinsingTime: 9, rinsingCount: 4, topSpinSpeed: "800" },
];

const DeviceSettingOptionTable = ({
    columnHeaders = defaultColumnHeaders,
    rowData = defaultRowData,
    data = mockData,
}) => {
    const [tableData, setTableData] = useState(data);
    const [selectedTwinSpray, setSelectedTwinSpray] = useState("enable");
    const [selectedTopOff, setSelectedTopOff] = useState("enable");
    const [selectedSensingDry, setSelectedSensingDry] = useState("enable");

    const handleValueChange = (colIndex, propertyName, newValue) => {
        setTableData((currentData) => {
            return currentData.map((item, index) => {
                if (index !== colIndex) {
                    return item;
                }
                return {
                    ...item,
                    [propertyName]: newValue,
                };
            });
        });
    };

    return (
        <Container>
            <HeaderRow length={columnHeaders.length + 1}>
                <RowHeader />
                {columnHeaders.map((header) => (
                    <TableHeader key={header.value}>{header.label}</TableHeader>
                ))}
            </HeaderRow>

            {rowData.map((headerText, index) => (
                <React.Fragment key={headerText.value}>
                    {index > 0 && <Divider />}

                    <Row length={columnHeaders.length + 1}>
                        <RowHeader>{headerText.label}</RowHeader>

                        {columnHeaders.map((col, idx) => (
                            <Cell key={`${headerText.value}-${col.value}`}>
                                {headerText.value === "washingTime" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "8", value: 8 },
                                            { label: "9", value: 9 },
                                            { label: "10", value: 10 },
                                            { label: "11", value: 11 },
                                            { label: "12", value: 12 },
                                        ]}
                                        inputValue={tableData[idx].washingTime}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "washingTime", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "waterLevel" && (
                                    <CustomSegmentedControl
                                        currentValue={tableData[idx].waterLevel}
                                        values={[
                                            { label: "Low", value: "low" },
                                            { label: "Mid", value: "medium" },
                                            { label: "High", value: "high" },
                                        ]}
                                        setValue={(newValue) => {
                                            handleValueChange(idx, "waterLevel", newValue);
                                        }}
                                        isDeparted={true}
                                    />
                                )}
                                {headerText.value === "rinsingTime" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "8", value: 8 },
                                            { label: "9", value: 9 },
                                            { label: "10", value: 10 },
                                            { label: "11", value: 11 },
                                            { label: "12", value: 12 },
                                        ]}
                                        inputValue={tableData[idx].rinsingTime}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "rinsingTime", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "rinsingCount" && (
                                    <CustomSegmentedControl
                                        currentValue={tableData[idx].rinsingCount}
                                        values={[
                                            { label: "1", value: 1 },
                                            { label: "2", value: 2 },
                                            { label: "3", value: 3 },
                                            { label: "4", value: 4 },
                                            { label: "5", value: 5 },
                                        ]}
                                        setValue={(newValue) => {
                                            handleValueChange(idx, "rinsingCount", newValue);
                                        }}
                                        isDeparted={true}
                                    />
                                )}
                                {headerText.value === "topSpinSpeed" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "Max", value: "Max" },
                                            { label: "1000", value: "1000" },
                                            { label: "900", value: "900" },
                                            { label: "800", value: "800" },
                                            { label: "700", value: "700" },
                                        ]}
                                        inputValue={tableData[idx].topSpinSpeed}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "topSpinSpeed", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "twinSpray" && idx === 0 && (
                                    <CustomRadios
                                        options={[
                                            { label: Translate("common.enable"), value: "enable" },
                                            { label: Translate("common.disable"), value: "disable" },
                                        ]}
                                        selectedOption={selectedTwinSpray}
                                        setSelectedOption={setSelectedTwinSpray}
                                    />
                                )}
                                {headerText.value === "regularPrice" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "0", value: 0 },
                                            { label: "1", value: 1 },
                                            { label: "2", value: 2 },
                                            { label: "3", value: 3 },
                                            { label: "4", value: 4 },
                                        ]}
                                        inputValue={tableData[idx].regularPrice}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "regularPrice", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "dryCycleTime" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "8", value: 8 },
                                            { label: "9", value: 9 },
                                            { label: "10", value: 10 },
                                            { label: "11", value: 11 },
                                            { label: "12", value: 12 },
                                        ]}
                                        inputValue={tableData[idx].dryCycleTime}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "dryCycleTime", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "topOffTime" && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "8", value: 8 },
                                            { label: "9", value: 9 },
                                            { label: "10", value: 10 },
                                            { label: "11", value: 11 },
                                            { label: "12", value: 12 },
                                        ]}
                                        inputValue={tableData[idx].topOffTime}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "topOffTime", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "topOffPrice" && idx !== 0 && (
                                    <CustomInput
                                        type={"select"}
                                        placeholder={""}
                                        dropdownValues={[
                                            { label: "0", value: 0 },
                                            { label: "1", value: 1 },
                                            { label: "2", value: 2 },
                                            { label: "3", value: 3 },
                                            { label: "4", value: 4 },
                                        ]}
                                        inputValue={tableData[idx].topOffPrice}
                                        setInputValue={(newValue) => {
                                            handleValueChange(idx, "topOffPrice", newValue);
                                        }}
                                        noDivider={true}
                                    />
                                )}
                                {headerText.value === "topOff" && idx === 0 && (
                                    <CustomRadios
                                        options={[
                                            { label: Translate("common.enable"), value: "enable" },
                                            { label: Translate("common.disable"), value: "disable" },
                                        ]}
                                        selectedOption={selectedTopOff}
                                        setSelectedOption={setSelectedTopOff}
                                    />
                                )}
                                {headerText.value === "sensingDry" && idx === 0 && (
                                    <CustomRadios
                                        options={[
                                            { label: Translate("common.enable"), value: "enable" },
                                            { label: Translate("common.disable"), value: "disable" },
                                        ]}
                                        selectedOption={selectedSensingDry}
                                        setSelectedOption={setSelectedSensingDry}
                                    />
                                )}
                            </Cell>
                        ))}
                    </Row>
                </React.Fragment>
            ))}
        </Container>
    );
};

export default DeviceSettingOptionTable;
